import type { LineIndex } from '../text/lineIndex';
import type { LintCheck, LintInput } from './check';
import { structureCheck } from './checks/structure';
import { semanticsCheck } from './checks/semantics';
import { tokensCheck } from './checks/tokens';
import { severityOf } from './codes';
import type {
  Diagnostic,
  DiagnosticDraft,
  RelatedSpan,
  RelatedSpanDraft,
  Severity,
} from './diagnostic';
import type { LintReport } from './report';

/**
 * The one place a `DiagnosticDraft` becomes a `Diagnostic`.
 *
 * A check emits a draft — a code, a span, some params. This file adds the two things a
 * check is not allowed to decide: `start`/`end`, computed from the `LineIndex`, and
 * `severity`, read from `DEFAULT_SEVERITY`. No check can get line arithmetic wrong because
 * no check does line arithmetic, and no check can call a warning an error (ADR-0008).
 *
 * Then it makes the result **deterministic**, which is a product requirement rather than a
 * tidiness one: a single analysis feeds the editor underlines, the status pill and the
 * problems panel at the same time (ADR-0009). If the order wobbled between runs, "jump to
 * next problem" would wobble with it, and the panel would reshuffle under the cursor.
 */

/** `error` before `warning` before `info`, at the same position. */
const SEVERITY_RANK: Readonly<Record<Severity, number>> = {
  error: 0,
  warning: 1,
  info: 2,
};

/**
 * The checks of v1, in pipeline order (ADR-0007 steps 4–6).
 *
 * Order does not affect the output — the engine sorts — but it is the order a reader should
 * follow, and it is the order in which the draft ceiling below fills up.
 */
export const DEFAULT_LINT_CHECKS: readonly LintCheck[] = [
  structureCheck,
  tokensCheck,
  semanticsCheck,
];

/**
 * Collection ceiling, as a multiple of `maxDiagnostics`.
 *
 * Sorting happens after collection, so the cap alone cannot bound memory: ten megabytes of
 * `,,,,,` would produce millions of drafts before the first comparison. The ceiling stops
 * collection early and sets `truncated`, so the report never claims to be complete when it
 * is not. The headroom exists because the checks do not interleave — the semantic check
 * runs last and would otherwise be starved by a lexically hostile document.
 */
const DRAFT_CEILING_FACTOR = 8;

export interface LintRun {
  /** Sorted, de-duplicated and capped. */
  readonly diagnostics: readonly Diagnostic[];
  readonly truncated: boolean;
}

function resolveRelated(
  drafts: readonly RelatedSpanDraft[],
  lines: LineIndex,
): readonly RelatedSpan[] {
  return drafts.map((draft) =>
    draft.params === undefined
      ? { span: draft.span, start: lines.positionAt(draft.span.from), labelCode: draft.labelCode }
      : {
          span: draft.span,
          start: lines.positionAt(draft.span.from),
          labelCode: draft.labelCode,
          params: draft.params,
        },
  );
}

function toDiagnostic(draft: DiagnosticDraft, lines: LineIndex): Diagnostic {
  const base = {
    code: draft.code,
    severity: severityOf(draft.code),
    span: draft.span,
    start: lines.positionAt(draft.span.from),
    end: lines.positionAt(draft.span.to),
  };

  // Built by spreading rather than by assigning `undefined`, because
  // `exactOptionalPropertyTypes` is on and because a `params: undefined` key would survive
  // `structuredClone` into the Worker response as noise.
  return {
    ...base,
    ...(draft.params === undefined ? {} : { params: draft.params }),
    ...(draft.related === undefined ? {} : { related: resolveRelated(draft.related, lines) }),
    ...(draft.fix === undefined ? {} : { fix: draft.fix }),
  };
}

/** Position, then width, then severity, then code. Total, so the sort is stable by value. */
function compare(a: Diagnostic, b: Diagnostic): number {
  if (a.span.from !== b.span.from) return a.span.from - b.span.from;
  if (a.span.to !== b.span.to) return a.span.to - b.span.to;

  const rank = SEVERITY_RANK[a.severity] - SEVERITY_RANK[b.severity];
  if (rank !== 0) return rank;

  return a.code < b.code ? -1 : a.code > b.code ? 1 : 0;
}

/**
 * Run every check over one input and finalise the result.
 *
 * The same `LintInput` goes to every check: nothing is re-lexed, re-parsed or re-indexed
 * per check, and a check cannot mutate what the next one sees.
 */
export function runLintChecks(
  input: LintInput,
  checks: readonly LintCheck[] = DEFAULT_LINT_CHECKS,
): LintRun {
  const ceiling = Math.max(input.options.maxDiagnostics, 1) * DRAFT_CEILING_FACTOR;
  const collected: Diagnostic[] = [];
  let overflowed = false;

  for (const check of checks) {
    if (collected.length >= ceiling) {
      overflowed = true;
      break;
    }

    check.run(input, (draft) => {
      if (collected.length >= ceiling) {
        overflowed = true;
        return;
      }
      collected.push(toDiagnostic(draft, input.lines));
    });
  }

  collected.sort(compare);

  // Exact duplicates — the same code over the same span — are one problem seen twice.
  // Sorting puts them next to each other, so collapsing is a linear scan.
  const unique: Diagnostic[] = [];
  for (const diagnostic of collected) {
    const previous = unique[unique.length - 1];
    if (
      previous !== undefined &&
      previous.code === diagnostic.code &&
      previous.span.from === diagnostic.span.from &&
      previous.span.to === diagnostic.span.to
    ) {
      continue;
    }
    unique.push(diagnostic);
  }

  const capped = unique.slice(0, input.options.maxDiagnostics);

  return { diagnostics: capped, truncated: overflowed || capped.length < unique.length };
}

/**
 * The serialisable report.
 *
 * `counts.fixable` counts diagnostics that carry a `fix`. Until `core/repair/registry.ts`
 * exists there are none, and the count is honestly zero rather than optimistically guessed
 * from the code — a code being *repairable in principle* is not the same as this occurrence
 * having a safe edit attached (ADR-0012).
 */
export function createLintReport(run: LintRun, elapsedMs: number): LintReport {
  let errors = 0;
  let warnings = 0;
  let infos = 0;
  let fixable = 0;

  for (const diagnostic of run.diagnostics) {
    if (diagnostic.severity === 'error') errors += 1;
    else if (diagnostic.severity === 'warning') warnings += 1;
    else infos += 1;

    if (diagnostic.fix !== undefined) fixable += 1;
  }

  return {
    ok: errors === 0,
    diagnostics: run.diagnostics,
    counts: { errors, warnings, infos, fixable },
    truncated: run.truncated,
    elapsedMs,
  };
}
